import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, useLocation, useNavigate, createSearchParams } from 'react-router-dom';
import AuthGuard from "./context/AuthGuard";
import WebsitePreviewLaunch from "./components/WebsitePreviewLaunch";
import CookieBanner from "./components/CookieBanner";
import SearchBarName from './components/SearchBarName';
import SearchBarDistance from './components/SearchBarDistance';
import DealerList from './components/DealerList';
import DealerDetails from './components/DealerDetails';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import Register from './components/Register';
import CreateAccount from './components/CreateAccount';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Claim from './components/Claim';
import BlogPost from './components/BlogPost';
import BlogList from './components/BlogList';

import About from './pages/About';
import Tou from './pages/Tou';
import Privacy from './pages/Privacy';
import Legal from './pages/Legal';
import HostingPolicy from './pages/HostingPolicy';
import SubscriberConfirmation from './pages/SubscriberConfirmation';
import {HostingTermsV11, DealerHostingOfferV10} from './pages/UnpublishedHostingPolicies';
import HostedWebsitePolicyDraftV1, {HOSTED_POLICY_DRAFT_ROUTES} from './pages/HostedWebsitePolicyDraftV1';

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp, faUser, faSignInAlt, faSignOutAlt, faDashboard,
  faBuilding, faCircleCheck, faHourglassHalf, faFileContract, faGauge,
  faClipboardCheck, faListAlt, faFolderOpen, faLock, faTimes, faUserShield
} from '@fortawesome/free-solid-svg-icons';
import Config from './utils/Config';
import { AuthProvider } from './context/AuthContext'; // ✅ Import AuthProvider
import './App.css';
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Add icons to the library (global usage)
library.add(
  faUser, faSignInAlt, faSignOutAlt, faDashboard,
  faBuilding, faCircleCheck, faHourglassHalf, faFileContract, faGauge,
  faClipboardCheck, faListAlt, faFolderOpen, faLock, faTimes, faUserShield
);

const App = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false); // State for "Up" button visibility
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();
  const navigate = useNavigate();

  const isDealerRoute = location.pathname.startsWith('/site/') || location.pathname.startsWith('/website-preview/');  

  useEffect(() => {
    // ✅ Clear the dealer list when navigating between search pages
    if (location.pathname === "/" || location.pathname === "/my-ffl") {
      setDealers([]);
      setCurrentPage(1);
    }
  }, [location.pathname]); // ✅ Trigger when route changes

  // Rehydrate results from URL params or clear when params are absent
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === "/name-search") {
      const q = (params.get("q") || "").trim();
      const s = (params.get("s") || "").trim();
      if (q || s) {
        handleSearch({ query: q, state: s }, { skipNav: true });
      } else {
        setDealers([]); setCurrentPage(1);
      }
    }
    if (location.pathname === "/") {
      const lat = parseFloat(params.get("lat"));
      const lng = parseFloat(params.get("lng"));
      const r = params.get("r") || "5";
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        handleMyFFLSearch({ lat, lng, radius: r }, { skipNav: true });
      } else {
        setDealers([]); setCurrentPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null; // Handle missing coordinates

    const toRad = (value) => (value * Math.PI) / 180;
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c); // Distance in miles
  };

  // Handle the search functionality for "My FFLs"
  const handleMyFFLSearch = async ({ lat, lng, radius }, opts = {}) => {
    if (!opts.skipNav) {
      navigate({
        pathname: "/",
        search: createSearchParams({
          lat: String(lat ?? ""),
          lng: String(lng ?? ""),
          r: String(radius ?? "")
        }).toString()
      }, { replace: false });
    }
    setLoading(true); // Start loading
    setCurrentPage(1);

    try {
      const response = await axios.get(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_SEARCH_RADIUS}`, {
        method: 'GET',
        params: {
          lat,
          lng,
          radius,
        },
      });

      // Add calculated distance to each dealer
      const dealersWithDistance = response.data
        .map((dealer) => {
          const dealerCoords = {
            lat: dealer.latitude, // Directly use latitude
            lng: dealer.longitude, // Directly use longitude
          };

          return {
            ...dealer,
            distance: dealerCoords.lat && dealerCoords.lng
              ? calculateDistance(lat, lng, dealerCoords.lat, dealerCoords.lng)
              : null,
          };
        })
        .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));

      setDealers(dealersWithDistance);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || 'An unexpected error occurred';

        if (status === 429) {
          // 🔒 Rate limit hit
          toast.error(`❌ ${message}`);
        } else {
          console.error('Search error:', message);
        }
      } else {
        console.error('Search request failed:', error.message);
      }
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Handle the search functionality
  const handleSearch = async ({ query, state }, opts = {}) => {
    const q = (query || "").trim();
    const s = (state || "").trim();

    // Update URL unless we came from the rehydration effect
    if (!opts.skipNav) {
      navigate({
        pathname: "/name-search",
        search: createSearchParams({ q, s }).toString()
      }, { replace: false });
    }

    // Clear signal
    if (!q && !s) { setDealers([]); setCurrentPage(1); return; }

    setLoading(true); // Start loading
    setCurrentPage(1);
    try {
      const response = await axios.get(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.SEARCH_DEALERS}`, {
        params: { query: q, state: s },
      });
      setDealers(response.data);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || 'An unexpected error occurred';

        if (status === 429) {
          // 🔒 Rate limit hit
          toast.error(`❌ ${message}`);
        } else {
          console.error('Search error:', message);
        }
      } else {
        console.error('Search request failed:', error.message);
      }
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Handle the beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Prevent the default mini-infobar
      setDeferredPrompt(e); // Save the event for later use
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const CLEAR_ON_ROUTES = new Set(['/', '/name-search', '/my-ffl']);
    if (CLEAR_ON_ROUTES.has(location.pathname)) {
      setDealers([]);
      setCurrentPage(1);
    }
  }, [location.pathname]);

  // Trigger the install prompt
  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt(); // Show the install prompt
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        setDeferredPrompt(null); // Reset the deferred prompt
      });
    }
  };

  // Scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show or hide the scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 300); // Show button if scrolled more than 300px
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        {!isDealerRoute && <Header />}
        <main>
          <CookieBanner />
          <Routes>
            <Route path="/mailing-list/confirm" element={<SubscriberConfirmation />} />
            <Route path="/hosting-terms" element={<HostingPolicy kind="terms" />} />
            <Route path="/hosting-refunds" element={<HostingPolicy kind="refunds" />} />
            <Route path="/hosting-terms/v1.1" element={<HostingTermsV11 />} />
            <Route path="/dealer-hosting-offer/v1.0" element={<DealerHostingOfferV10 />} />
            <Route path="/hosting-terms/v1.0" element={<HostingPolicy kind="terms" showCurrentOffer={false} />} />
            <Route path="/hosting-refunds/v1.0" element={<HostingPolicy kind="refunds" showCurrentOffer={false} />} />
            <Route path={HOSTED_POLICY_DRAFT_ROUTES.subscription} element={<HostedWebsitePolicyDraftV1 kind="subscription" />} />
            <Route path={HOSTED_POLICY_DRAFT_ROUTES.cancellation} element={<HostedWebsitePolicyDraftV1 kind="cancellation" />} />
            <Route path="/website-preview/:claimId" element={<WebsitePreviewLaunch />} />
            {/* Home Route now renders MyFFLSearchBar */}
            <Route
              path="/"
              element={
                <>
                  <SearchBarDistance
                    onSearch={handleMyFFLSearch}
                    setCurrentPage={setCurrentPage}
                    onClear={() => {
                      setDealers([]); setCurrentPage(1);
                      navigate({ pathname: "/", search: "" }, { replace: false });
                    }}
                  />
                  <DealerList dealers={dealers} loading={loading} searchBarType="myFFLSearchBar" showDistanceSort={true} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                  {deferredPrompt && (
                    <button onClick={handleInstallClick} className="install-button">
                      Install App
                    </button>
                  )}
                </>
              }
            />
            {/* "/name-search" Route now renders SearchBar */}
            <Route
              path="/name-search"
              element={
                <>
                  <SearchBarName
                    onSearch={handleSearch}
                    onClear={() => {
                      setDealers([]);
                      setCurrentPage(1);
                    }}
                  />
                  <DealerList dealers={dealers} loading={loading} searchBarType="searchBar" currentPage={currentPage} setCurrentPage={setCurrentPage} />
                  {deferredPrompt && (
                    <button onClick={handleInstallClick} className="install-button">
                      Install App
                    </button>
                  )}
                </>
              }
            />
            <Route
              path="/login"
              element={<Login />}
            />
            <Route
              path="/register"
              element={<Register />}
            />
            <Route
              path="/create/:token"
              element={<CreateAccount />}
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/reset/:token"
              element={<ResetPassword />}
            />
            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <Dashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/claim/:claimid"
              element={
                <AuthGuard>
                  <Claim />
                </AuthGuard>
              }
            />
            <Route
              path="/dealer/:id"
              element={<DealerDetails />}
            />
            <Route
              path="/blog"
              element={<BlogList />}
            />
            <Route
              path="/blog/:blogId"
              element={<BlogPost />}
            />
            <Route
              path="/about"
              element={<About />}
            />
            <Route
              path="/terms"
              element={<Tou />}
            />
            <Route
              path="/privacy"
              element={<Privacy />}
            />
            <Route
              path="/legal"
              element={<Legal />}
            />
          </Routes>
        </main>
        {!isDealerRoute && <Footer />}
        {/* Scroll-to-Top Button */}
        {showScrollButton && (
          <button className="cookie-banner-visible scroll-to-top" onClick={scrollToTop}>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
        )}
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
        />
      </div>
    </AuthProvider>
  );
};

export default App;